import React, { useState } from "react";
import FilterBar, { computeRange, useSharedFilter } from "../FilterBar";
import { buildParams } from "../api";
import { useAdminData } from "../useAdminData";
import { Panel, Skeleton, EmptyState, ErrorState, PageHeader, fmtNum, fmtPct } from "../Primitives";
import { GAME_BY_ID, platformLabel, PROVIDER_NAMES } from "../catalog";

const DIMS = [
  { key: "source", label: "Origem", help: "utm_source; sem UTM, usa o domínio de referência; sem ambos, \"direto\"." },
  { key: "campaign", label: "Campanha", help: "utm_campaign" },
  { key: "content", label: "Influenciador / grupo", help: "utm_content — use um valor por influenciador ou grupo." },
  { key: "medium", label: "Mídia", help: "utm_medium" },
  { key: "term", label: "Termo", help: "utm_term" },
  { key: "referrer", label: "Referenciador", help: "Domínio de onde o visitante veio (quando o navegador informa)." },
  { key: "platform", label: "Plataformas", help: "Sessões com algum evento ligado à plataforma." },
  { key: "provider", label: "Provedores", help: "Sessões com algum evento ligado ao provedor." },
  { key: "game", label: "Jogos", help: "Sessões com algum evento ligado ao jogo." },
];

const label = (dim, row) => {
  if (row.key == null) return <span className="text-[var(--muted2)]">(não informado)</span>;
  if (dim === "platform") return platformLabel(row.key);
  if (dim === "provider") return PROVIDER_NAMES[row.key] || row.key;
  if (dim === "game") return row.name || GAME_BY_ID[row.key]?.name || row.key;
  return row.key;
};

const defaultFilter = () => ({ preset: "30d", ...computeRange("30d"), platform: "", provider: "", game: "", device: "", campaign: "", source: "" });

export default function Traffic() {
  const [filter, setFilter] = useSharedFilter(defaultFilter);
  const [dim, setDim] = useState("source");
  const { data, loading, error, reload } = useAdminData(
    async (api) => (await api.get("/admin/traffic", { params: buildParams(filter) })).data.dimensions, [filter]);
  const rows = data?.[dim] || [];
  const meta = DIMS.find((d) => d.key === dim);

  return (
    <div data-testid="traffic-page">
      <PageHeader title="Tráfego e campanhas" meta="Rankings por origem, UTM e entidade, com taxas calculadas por sessão." />
      <FilterBar value={filter} onChange={setFilter} show={{ game: true, campaign: true, source: true }} />
      {error && <ErrorState message={error} onRetry={reload} />}

      <div className="flex flex-wrap gap-1 mb-3" role="tablist" aria-label="Dimensão">
        {DIMS.map((d) => (
          <button key={d.key} role="tab" aria-selected={dim === d.key} data-testid={`dim-${d.key}`} onClick={() => setDim(d.key)}
            className={`ops-btn ops-btn-sm ${dim === d.key ? "ops-btn-outline" : "ops-btn-ghost"}`}>{d.label}</button>
        ))}
      </div>

      <Panel title={meta.label} description={meta.help} testid="traffic-table-panel">
        {loading && !data ? <div className="p-4 space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8" />)}</div>
          : rows.length === 0 ? <EmptyState title="Sem dados para esta dimensão" description="Links com parâmetros UTM (ex.: ?utm_source=instagram&utm_campaign=junho&utm_content=nome_do_influenciador) passam a aparecer aqui." />
          : (
            <div className="overflow-x-auto mia-scroll">
              <table className="ops-table" data-testid="traffic-table">
                <thead><tr>
                  <th>#</th><th>{meta.label}</th><th className="!text-right">Sessões</th><th className="!text-right">Eventos</th>
                  <th className="!text-right">Abriram jogo</th><th className="!text-right">Clicaram p/ plataforma</th><th className="!text-right">Cliques de saída</th>
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.key}-${i}`} className="mia-row">
                      <td className="mono text-[var(--muted2)] w-8">{i + 1}</td>
                      <td className="max-w-[260px] truncate">{label(dim, r)}</td>
                      <td className="num">{fmtNum(r.sessions)}</td>
                      <td className="num text-[var(--muted)]">{fmtNum(r.events)}</td>
                      <td className="num">{fmtPct(r.game_rate)}</td>
                      <td className="num">{fmtPct(r.exit_rate)}</td>
                      <td className="num text-[var(--muted)]">{fmtNum(r.exit_clicks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        <p className="px-4 py-3 ops-help border-t border-[var(--border)]">
          Taxas = sessões com ao menos uma abertura de jogo (ou clique de saída) ÷ sessões da linha. Eventos anteriores a esta versão não têm origem registrada.
        </p>
      </Panel>
    </div>
  );
}
